/* ==========================================================================
   FLEXRA SAFETY - CAREERS FORM SUBMISSION SCRIPT
   File: assets/careers-form.js

   Upload to: Shopify Admin > Online Store > Themes > Edit code > Assets >
              "Add a new asset" > name it exactly:  careers-form.js

   WHAT IT DOES
   ------------
   * Intercepts the careers form submit
   * Sends the data (including the resume file) with fetch() + FormData to the
     PHP backend on Hostinger
   * Shows a "Submitting..." state and disables the button
   * Shows the per-field errors returned by the server
   * On success: resets the form and shows the success message
   * On failure: keeps everything the user typed

   SECURITY NOTES
   --------------
   * No secret, no API key and no SMTP credential is present in this file.
     It is public JavaScript - it must never contain credentials.
   * Content-Type is deliberately NOT set on the fetch() call. The browser
     must generate the multipart boundary itself.
   ========================================================================== */

(function () {
  'use strict';

  var FORM_SELECTOR = '[data-fxcareers-form]';
  var initialized = [];

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  function findStatus(form) {
    return form.querySelector('[data-fxcareers-status]');
  }

  function findButton(form) {
    return form.querySelector('[data-fxcareers-submit]') || form.querySelector('button[type="submit"]');
  }

  function showStatus(form, type, message) {
    var box = findStatus(form);
    if (!box) {
      return;
    }
    box.className = 'fxcareers__status is-visible ' + (type === 'ok' ? 'is-success' : 'is-error');
    box.textContent = message;

    if (type === 'ok') {
      box.setAttribute('role', 'status');
    } else {
      box.setAttribute('role', 'alert');
    }

    // Bring the message into view on small screens.
    try {
      box.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch (e) {
      /* older browsers: ignore */
    }
  }

  function hideStatus(form) {
    var box = findStatus(form);
    if (!box) {
      return;
    }
    box.className = 'fxcareers__status';
    box.textContent = '';
  }

  function clearFieldErrors(form) {
    var nodes = form.querySelectorAll('[data-error-for]');
    Array.prototype.forEach.call(nodes, function (node) {
      node.textContent = '';
      node.classList.remove('is-visible');
    });
    var inputs = form.querySelectorAll('[aria-invalid]');
    Array.prototype.forEach.call(inputs, function (input) {
      input.removeAttribute('aria-invalid');
    });
  }

  function showFieldErrors(form, errors) {
    if (!errors || typeof errors !== 'object') {
      return;
    }
    Object.keys(errors).forEach(function (field) {
      var node = form.querySelector('[data-error-for="' + field + '"]');
      if (!node) {
        return;
      }
      node.textContent = errors[field];
      node.classList.add('is-visible');

      var input = form.querySelector('[name="' + field + '"]');
      if (input) {
        input.setAttribute('aria-invalid', 'true');
      }
    });
  }

  function setBusy(form, busy) {
    var button = findButton(form);
    if (!button) {
      return;
    }
    if (busy) {
      button.dataset.originalLabel = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = form.dataset.submittingLabel || 'Submitting...';
    } else {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = button.dataset.originalLabel
        || form.dataset.submitLabel
        || 'Submit application';
    }
  }

  function humanSize(bytes) {
    if (bytes < 1024) { return bytes + ' B'; }
    if (bytes < 1048576) { return (bytes / 1024).toFixed(0) + ' KB'; }
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  function stamp(form) {
    var field = form.querySelector('[data-fxcareers-timestamp]');
    if (field) {
      field.value = String(Date.now());
    }
  }

  function initForm(form) {
    if (initialized.indexOf(form) !== -1) {
      return;
    }
    initialized.push(form);

    var maxBytes = parseInt(form.dataset.maxBytes || '10485760', 10);
    var successMessage = form.dataset.successMessage || 'Application submitted successfully.';
    var genericError = form.dataset.errorMessage || 'Unable to submit your application. Please try again.';

    // Record when the visitor opened the form (server side bot check).
    stamp(form);

    // Warn about an oversized file before wasting bandwidth.
    var fileInput = form.querySelector('input[name="resume"]');
    if (fileInput) {
      fileInput.addEventListener('change', function () {
        hideStatus(form);
        var file = fileInput.files && fileInput.files[0];
        if (file && file.size > maxBytes) {
          showStatus(
            form,
            'err',
            'That file is ' + humanSize(file.size) + '. The maximum resume size is '
              + humanSize(maxBytes) + '. Please choose a smaller file.'
          );
          fileInput.value = '';
        }
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      event.stopPropagation();

      hideStatus(form);
      clearFieldErrors(form);

      var endpoint = (form.getAttribute('action') || '').trim();
      if (endpoint.indexOf('http') !== 0 || endpoint.indexOf('YOUR-HOSTINGER-DOMAIN') !== -1) {
        showStatus(form, 'err', 'This form is not configured yet. Please contact the site administrator.');
        return;
      }

      // Basic browser side checks (the server repeats every one of them).
      var missing = [];
      ['name', 'email', 'phone', 'city', 'state'].forEach(function (field) {
        var input = form.querySelector('[name="' + field + '"]');
        if (input && input.value.trim() === '') {
          missing.push(field);
        }
      });

      var resumeField = form.querySelector('input[name="resume"]');
      if (resumeField && (!resumeField.files || resumeField.files.length === 0)) {
        missing.push('resume');
      }

      if (missing.length > 0) {
        var messages = {
          name: 'Please enter your full name.',
          email: 'Please enter your email address.',
          phone: 'Please enter your phone number.',
          city: 'Please enter your city.',
          state: 'Please enter your state.',
          resume: 'Please attach your resume (PDF, DOC or DOCX).'
        };
        var errors = {};
        missing.forEach(function (field) {
          errors[field] = messages[field] || (field + ' is required.');
        });
        showFieldErrors(form, errors);
        showStatus(form, 'err', 'Please complete the required fields.');
        return;
      }

      var file = resumeField.files[0];
      if (file && file.size > maxBytes) {
        showStatus(
          form,
          'err',
          'That file is ' + humanSize(file.size) + '. The maximum resume size is ' + humanSize(maxBytes) + '.'
        );
        return;
      }

      // Fresh timing token for every attempt.
      stamp(form);
      setBusy(form, true);

      var formData = new FormData(form);

      // Abort if the server has not answered after 2 minutes (large upload).
      var controller = null;
      var timeoutId = null;
      var fetchOptions = {
        method: 'POST',
        body: formData,
        mode: 'cors',
        credentials: 'omit',
        redirect: 'follow',
        cache: 'no-store'
        // NOTE: Content-Type is intentionally NOT set.
      };

      if (typeof AbortController === 'function') {
        controller = new AbortController();
        fetchOptions.signal = controller.signal;
        timeoutId = setTimeout(function () {
          controller.abort();
        }, 120000);
      }

      fetch(endpoint, fetchOptions)
        .then(function (response) {
          return response.text().then(function (raw) {
            var data = null;
            try {
              data = JSON.parse(raw);
            } catch (e) {
              data = null;
            }
            return { response: response, data: data, raw: raw };
          });
        })
        .then(function (result) {
          var data = result.data;

          if (data && data.success === true) {
            showStatus(form, 'ok', successMessage);
            form.reset();
            stamp(form);
            clearFieldErrors(form);
            return;
          }

          // Server side validation errors.
          if (data && data.errors) {
            showFieldErrors(form, data.errors);
          }

          var message = genericError;
          if (data && typeof data.message === 'string' && data.message !== '') {
            // Only show server messages that are clearly meant for the user.
            message = data.message;
          } else if (result.response.status === 413) {
            message = 'Your file is too large. The maximum resume size is ' + humanSize(maxBytes) + '.';
          } else if (result.response.status === 429) {
            message = 'Too many attempts. Please wait a few minutes and try again.';
          } else if (result.response.status >= 500) {
            message = 'Unable to submit your application. Please try again later.';
          }

          showStatus(form, 'err', message);
        })
        .catch(function (error) {
          // Network failure, CORS block, DNS problem, timeout.
          var message = genericError;
          if (error && error.name === 'AbortError') {
            message = 'The request took too long. Please check your connection and try again.';
          }
          showStatus(form, 'err', message);

          if (window.console && typeof console.error === 'function') {
            console.error('[FlexraCareers] submission failed:', error);
          }
        })
        .finally(function () {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          setBusy(form, false);
        });
    });
  }

  onReady(function () {
    var forms = document.querySelectorAll(FORM_SELECTOR);
    Array.prototype.forEach.call(forms, initForm);

    // Shopify section editor: re-bind when a section is re-rendered.
    document.addEventListener('shopify:section:load', function (event) {
      if (!event || !event.target) {
        return;
      }
      var scoped = event.target.querySelectorAll ? event.target.querySelectorAll(FORM_SELECTOR) : [];
      Array.prototype.forEach.call(scoped, initForm);
    });
  });
})();
